import { useEffect, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Euler, Quaternion } from 'three';

// Animate relative to the imported rest pose, with no accumulated frame drift.
export default function HeadAnimator({ avatarRef, playing, animationConfig = {} }) {
  const cache = useRef({ bones: {}, meshes: [], time: 0, nextBlink: 2.5, blinkStart: -10 });
  const offset = useRef(new Quaternion());
  const euler = useRef(new Euler());
  useEffect(() => {
    const state = { bones: {}, meshes: [], time: 0, nextBlink: 2.5, blinkStart: -10 };
    avatarRef?.traverse(node => {
      if (node.isBone && ['head', 'neck', 'spine.002', 'upper_arm.L', 'upper_arm.R'].includes(node.name)) {
        state.bones[node.name] = { node, rest: node.quaternion.clone() };
      }
      if (node.morphTargetDictionary) state.meshes.push(node);
    });
    cache.current = state;
    return () => Object.values(state.bones).forEach(({ node, rest }) => node.quaternion.copy(rest));
  }, [avatarRef]);
  useFrame((_, delta) => {
    if (!avatarRef) return;
    const state = cache.current;
    state.time += Math.min(delta, 0.1);
    const t = state.time;
    const cfg = animationConfig;
    const speaking = playing ? cfg.subtleFactor ?? 0.2 : 1;
    const animate = (name, x, y, z) => {
      const bone = state.bones[name];
      if (!bone) return;
      offset.current.setFromEuler(euler.current.set(x, y, z));
      bone.node.quaternion.copy(bone.rest).multiply(offset.current);
    };
    const h = (cfg.headAmplitude ?? 1) * speaking;
    const n = (cfg.neckAmplitude ?? 1) * speaking;
    const ht = t * (cfg.headSpeed ?? 1);
    const nt = t * (cfg.neckSpeed ?? 1);
    animate('head', Math.sin(ht * 0.7) * 0.016 * h, Math.sin(ht * 0.43) * 0.024 * h, Math.sin(ht * 0.51) * 0.012 * h);
    animate('neck', Math.sin(nt * 0.55) * 0.006 * n, Math.sin(nt * 0.31) * 0.008 * n, 0);
    animate('spine.002', Math.sin(t * 1.5) * 0.003, 0, Math.sin(t * 0.4) * 0.002);
    animate('upper_arm.L', Math.sin(t * 0.8) * 0.004, 0, Math.sin(t * 0.55) * 0.005);
    animate('upper_arm.R', Math.sin(t * 0.8 + 1) * 0.004, 0, -Math.sin(t * 0.55) * 0.005);
    if (t >= state.nextBlink) {
      state.blinkStart = t;
      const min = cfg.blinkIntervalMin ?? 2;
      const max = Math.max(min, cfg.blinkIntervalMax ?? 4);
      state.nextBlink = t + min + Math.random() * (max - min);
    }
    const phase = (t - state.blinkStart) * (cfg.blinkSpeed ?? 15);
    const blink = phase < 1 ? phase : phase < 2 ? 2 - phase : 0;
    const gazeX = Math.sin(t * 0.62) * 0.28;
    const gazeY = Math.sin(t * 0.47 + 1) * 0.18;
    state.meshes.forEach(mesh => {
      const set = (name, value) => {
        const index = mesh.morphTargetDictionary[name];
        if (index !== undefined) mesh.morphTargetInfluences[index] = Math.max(0, value);
      };
      set('eyeBlink_L', blink); set('eyeBlink_R', blink);
      ['Left', 'Right'].forEach(side => {
        set(`eyeLookUp${side}`, gazeY); set(`eyeLookDown${side}`, -gazeY);
        const horizontal = side === 'Left' ? gazeX : -gazeX;
        set(`eyeLookOut${side}`, horizontal); set(`eyeLookIn${side}`, -horizontal);
      });
    });
  });
  return null;
}
